import React from "react";
import { Phone, Mail } from "lucide-react";
import { toast } from "react-toastify";
import { Loading } from "../../../components/loading";
import { EmptyList } from "../../../components/empty-list";
import { Table, TableHeader, TableBody, TableRow, TableCell } from "../../../components/tables";
import { useProspections, useRemoveProspection } from "../hooks";
import { useCustomers } from "../../customers/hooks";
import { useMe } from "../../users/hooks/useMe";
import { UserRole, ProspectionType } from "../../../__generated__/globalTypes";
import { prospectionTypeLabel } from "../../../helpers/enum-labels";

interface IProps {
  customerId?: number;
}

export const ProspectionsList: React.FC<IProps> = ({ customerId }) => {
  const { data, loading, refetch } = useProspections({ where: { customerId } });
  const { data: customersData } = useCustomers({ where: {} });
  const { data: meData } = useMe();
  const [remove] = useRemoveProspection();

  const isAdmin = meData?.me?.role === UserRole.Admin;

  const handleRemove = async (id: number) => {
    if (!window.confirm("Supprimer cette prospection ? Cette action est irréversible.")) return;
    try {
      const { data } = await remove({ variables: { id } });
      if (data?.removeProspection?.ok) {
        toast.success("La prospection a été supprimée");
        refetch();
      } else {
        toast.error(data?.removeProspection?.error ?? "Une erreur est survenue");
      }
    } catch (error: any) {
      toast.error(error?.message ?? "Une erreur est survenue");
    }
  };

  if (loading) return <Loading />;

  if (!data?.prospections?.results?.length) {
    return <EmptyList text="Aucune prospection" />;
  }

  const customerName = (id: number | null) =>
    customersData?.customers?.results?.find((customer) => customer.id === id)?.name ?? `#${id}`;

  return (
    <Table>
      <TableHeader>
        <TableRow hover={false}>
          <TableCell header>Type</TableCell>
          <TableCell header>Contact</TableCell>
          {!customerId && <TableCell header>Client</TableCell>}
          <TableCell header>Message</TableCell>
          <TableCell header>Envoyé par</TableCell>
          {isAdmin && <TableCell header align="center">Actions</TableCell>}
        </TableRow>
      </TableHeader>
      <TableBody>
        {data?.prospections?.results?.map((prospection) => (
          <TableRow key={`prospection-${prospection.id}`} hover={false}>
            <TableCell>
              <span className="inline-flex items-center gap-1.5">
                {prospection.type === ProspectionType.Email ? (
                  <Mail className="w-4 h-4 text-gray-500" />
                ) : (
                  <Phone className="w-4 h-4 text-gray-500" />
                )}
                {prospectionTypeLabel(prospection.type)}
              </span>
            </TableCell>
            <TableCell>{prospection.email || prospection.phone || "-"}</TableCell>
            {!customerId && (
              <TableCell>
                {prospection.customerId ? customerName(prospection.customerId) : "-"}
              </TableCell>
            )}
            <TableCell className="max-w-xs truncate">{prospection.message}</TableCell>
            <TableCell>
              {prospection.sentBy
                ? `${prospection.sentBy.firstname} ${prospection.sentBy.lastname}`
                : "-"}
            </TableCell>
            {isAdmin && (
              <TableCell align="center">
                <span
                  className="text-red-600 text-sm cursor-pointer hover:underline"
                  onClick={() => handleRemove(prospection.id)}
                >
                  Supprimer
                </span>
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
